/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * useSettingsStore.tsx - hook for settings state
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface SettingsStoreState {
  // State
  /** Whether the store has been hydrated with persisted storage at least once. */
  _hasHydrated: boolean;
  /** Whether audio is enabled. */
  audioEnabled: boolean;

  // Methods
  setAudioEnabled: (enabled: boolean) => void;
  toggleAudioEnabled: () => void;
  setHasHydrated: (hasHydrated: boolean) => void;
}

/**
 * Hook for the settings store.
 *
 * See documentation of zustand for more information: https://github.com/pmndrs/zustand/
 *
 * Note that we persist with AsyncStorage. This means that hydration is asynchronous - see
 * https://github.com/pmndrs/zustand/wiki/Persisting-the-store's-data#hydration-and-asynchronous-storages.
 *
 * This means that after calling for the store to be hydrated (such as when the app first
 * starts) there will be a small time for which the store shows the default values listed below, even if
 * some other state exists in storage.
 *
 * To solve this, we wait for the first hydration to complete in App.tsx before marking the app as
 * loaded. All of the other screens can safely assume that this has occurred.
 */
const useSettingsStore = create<SettingsStoreState>()(
  // Middleware to persist state to storage
  persist(
    set => ({
      // Default state (overridden by state in storage after store hydration)
      _hasHydrated: false,
      audioEnabled: true,

      // Methods
      setAudioEnabled: (enabled: boolean) => set({
        audioEnabled: enabled
      }),
      toggleAudioEnabled: () => set(prevState => ({
        audioEnabled: !prevState.audioEnabled
      })),
      setHasHydrated: (hasHydrated: boolean) => set({
        _hasHydrated: hasHydrated
      }),
    }),
    // persist middleware configuration
    {
      name: 'settings-storage', // unique key in AsyncStorage,
      storage: createJSONStorage(() => AsyncStorage),
      onRehydrateStorage: () => (state, error) => {
        if (typeof error !== 'undefined') {
          // Store rehydration failed!
          // Trying to carry on without loading persisted settings feels like a poor alternative, so just crash.
          // This should never happen, and the best fix is for the user to restart the app anyway.
          throw error;
        } else if (typeof state !== 'undefined') {
          state.setHasHydrated(true);
        } else {
          // From reading the source code, if there's no error, the state must be defined.
          throw new Error('Unreachable: neither error nor state provided.');
        }
      }
    }
  )
);

export default useSettingsStore;
